import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { MapPin, Maximize2, ArrowLeft, ArrowRight, Phone, CheckCircle2, Home, Calendar } from "lucide-react";
import { allProjects, statusConfig } from "../data/projects";
import { motion } from "framer-motion";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = allProjects.find((p) => p.slug === slug);

  if (!project) {
    return (
      <Layout>
        <div className="py-40 text-center">
          <h2 className="text-3xl font-bold text-primary mb-4">Project Not Found</h2>
          <Link to="/projects" className="inline-flex items-center gap-2 text-secondary font-semibold hover:underline">
            <ArrowLeft size={18} /> Back to Projects
          </Link>
        </div>
      </Layout>
    );
  }

  const status = statusConfig[project.status];

  // Get next & prev projects
  const currentIndex = allProjects.findIndex((p) => p.slug === slug);
  const prevProject = allProjects[currentIndex - 1];
  const nextProject = allProjects[currentIndex + 1];

  return (
    <Layout>
      {/* Hero Image */}
      <section className="relative h-[55vh] md:h-[65vh] overflow-hidden">
        <img
          src={project.image}
          alt={project.name}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* Back button */}
        <button
          onClick={() => navigate("/projects")}
          data-testid="back-to-projects"
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium hover:bg-white/30 transition-colors border border-white/30"
        >
          <ArrowLeft size={16} /> All Projects
        </button>

        {/* Project name overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${status.color}`}>
              {status.label}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-2">{project.name}</h1>
            <div className="flex items-center gap-2 text-gray-200 text-sm">
              <MapPin size={14} className="text-secondary" />
              <span>{project.address}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Left: Details */}
            <div className="lg:col-span-2 space-y-10">

              {/* Quick stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="grid grid-cols-2 md:grid-cols-3 gap-4"
              >
                {[
                  { icon: MapPin, label: "Location", value: project.city },
                  { icon: Maximize2, label: "Area", value: project.area },
                  { icon: Calendar, label: "Status", value: status.label },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} data-testid={`stat-card-${i}`} className="bg-gray-50 rounded-xl p-4 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={16} className="text-secondary" />
                        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{item.label}</span>
                      </div>
                      <p className="font-semibold text-primary text-sm">{item.value}</p>
                    </div>
                  );
                })}
              </motion.div>

              {/* About the Project */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <h2 className="text-2xl font-bold text-primary mb-4">About the Project</h2>
                <p className="text-muted-foreground leading-relaxed text-base">{project.description}</p>
              </motion.div>

              {/* Highlights */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <h2 className="text-2xl font-bold text-primary mb-4">Project Highlights</h2>
                <ul className="space-y-3">
                  {project.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{h}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Configurations */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <h2 className="text-2xl font-bold text-primary mb-4">Configurations</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {project.configurations.map((cfg, i) => (
                    <div key={i} data-testid={`config-${i}`} className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-center">
                      <div className="w-10 h-10 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Home size={18} className="text-secondary" />
                      </div>
                      <p className="font-bold text-primary">{cfg.type}</p>
                      <p className="text-sm text-muted-foreground">{cfg.area}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Amenities */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <h2 className="text-2xl font-bold text-primary mb-4">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {project.amenities.map((a, i) => (
                    <span key={i} className="px-3 py-1.5 bg-secondary/15 text-primary text-sm font-medium rounded-full border border-secondary/30">
                      {a}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right: Enquiry Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
                className="sticky top-24 bg-white rounded-2xl border border-border shadow-lg p-6"
              >
                <h3 className="text-xl font-bold text-primary mb-2">Interested in this project?</h3>
                <p className="text-muted-foreground text-sm mb-6">Get in touch with us for pricing, availability and site visits.</p>

                <div className="space-y-3 mb-6">
                  <input
                    type="text"
                    placeholder="Your Name"
                    data-testid="enquiry-name"
                    className="w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    data-testid="enquiry-phone"
                    className="w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    data-testid="enquiry-email"
                    className="w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                  />
                </div>

                <button
                  data-testid="enquiry-submit"
                  className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:opacity-90 transition-opacity mb-3"
                  onClick={() => alert("Thank you! We'll contact you shortly.")}
                >
                  Request Callback
                </button>

                <a
                  href="tel:+918976004522"
                  data-testid="call-now-btn"
                  className="w-full py-3 bg-secondary text-secondary-foreground font-semibold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm"
                >
                  <Phone size={16} /> Call: +91 89760 04522
                </a>

                <div className="mt-6 pt-6 border-t border-border text-center">
                  <p className="text-xs text-muted-foreground">Arrow Construction</p>
                  <p className="text-xs text-muted-foreground">info@arrowconstruction.in</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Prev / Next Navigation */}
      <section className="bg-gray-50 py-10 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {prevProject ? (
              <Link
                to={`/projects/${prevProject.slug}`}
                data-testid="prev-project-btn"
                className="flex items-center gap-3 group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                  <ArrowLeft size={18} className="text-primary group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Previous Project</p>
                  <p className="font-semibold text-primary text-sm">{prevProject.name}</p>
                </div>
              </Link>
            ) : <div />}

            {nextProject ? (
              <Link
                to={`/projects/${nextProject.slug}`}
                data-testid="next-project-btn"
                className="flex items-center gap-3 group text-right"
              >
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Next Project</p>
                  <p className="font-semibold text-primary text-sm">{nextProject.name}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-all">
                  <ArrowRight size={18} className="text-primary group-hover:text-white" />
                </div>
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Want to see more projects?</h2>
          <p className="text-gray-200 mb-8">Explore our full portfolio of residential projects across Mumbai and Navi Mumbai.</p>
          <Link to="/projects" data-testid="view-all-btn" className="inline-flex items-center gap-2 px-8 py-3 bg-secondary text-secondary-foreground font-bold rounded-lg hover:opacity-90 transition-opacity">
            View All Projects <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
